import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCheck, Bell, Receipt, Download, RotateCcw, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const STATUS_STYLE = {
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-gold-500/10 text-gold-700 border-gold-200",
    failed: "bg-red-50 text-red-700 border-red-200",
    expired: "bg-navy-50 text-navy-500 border-navy-100",
    refunded: "bg-red-50 text-red-700 border-red-200",
};

function money(amount, currency) {
    if (amount == null || !currency) return "—";
    return `${currency.toUpperCase()} ${(amount / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

function fmt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function AdminTransactions() {
    const [transactions, setTransactions] = useState([]);
    const [notif, setNotif] = useState({ unread: 0, items: [] });
    const [loading, setLoading] = useState(true);
    const [month, setMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    });
    const [reimbursement, setReimbursement] = useState(null);
    const [refundTx, setRefundTx] = useState(null);
    const [refundReason, setRefundReason] = useState("requested_by_customer");
    const [refunding, setRefunding] = useState(false);
    // Filters for the Transactions tab
    const [txFrom, setTxFrom] = useState("");
    const [txTo, setTxTo] = useState("");
    const [txVendor, setTxVendor] = useState("all");
    const [txStatus, setTxStatus] = useState("all");
    const [txQuery, setTxQuery] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [t, n, r] = await Promise.all([
                api.get("/admin/transactions"),
                api.get("/admin/notifications"),
                api.get(`/admin/reimbursements?month=${month}`),
            ]);
            setTransactions(t.data);
            setNotif(n.data);
            setReimbursement(r.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [month]);

    const markAllRead = async () => {
        try {
            await api.post("/admin/notifications/read-all");
            toast.success("All notifications marked as read");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const markRead = async (id) => {
        try {
            await api.post(`/admin/notifications/${id}/read`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const submitRefund = async () => {
        if (!refundTx) return;
        setRefunding(true);
        try {
            await api.post(`/admin/transactions/${refundTx.session_id}/refund`, {
                reason: refundReason,
            });
            toast.success("Refund issued");
            setRefundTx(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRefunding(false);
        }
    };

    const totalPaid = transactions
        .filter((t) => t.payment_status === "paid")
        .reduce((s, t) => s + (t.amount || 0), 0);
    const paidCount = transactions.filter((t) => t.payment_status === "paid").length;

    // Vendor list for the filter dropdown (dedupe, sort)
    const vendors = useMemo(() => {
        const s = new Set();
        transactions.forEach((t) => t.vendor && s.add(t.vendor));
        return Array.from(s).sort();
    }, [transactions]);

    // Apply date + vendor + status + search filters to the Transactions tab
    const filteredTx = useMemo(() => {
        const fromTs = txFrom ? new Date(txFrom + "T00:00:00").getTime() : null;
        const toTs = txTo ? new Date(txTo + "T23:59:59.999").getTime() : null;
        const q = txQuery.trim().toLowerCase();
        return transactions.filter((t) => {
            if (txVendor !== "all" && (t.vendor || "") !== txVendor) return false;
            if (txStatus !== "all" && (t.payment_status || "") !== txStatus) return false;
            const ts = new Date(t.created_at).getTime();
            if (fromTs && ts < fromTs) return false;
            if (toTs && ts > toTs) return false;
            if (q) {
                const hay = [
                    t.product_name,
                    t.vendor,
                    t.customer_email,
                    t.session_id,
                    t.lookup_key,
                    t.payment_status,
                    t.mode,
                ]
                    .join(" ")
                    .toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [transactions, txFrom, txTo, txVendor, txStatus, txQuery]);

    const filteredTotal = filteredTx.reduce(
        (s, t) => s + (t.payment_status === "paid" ? t.amount || 0 : 0),
        0
    );
    const filtersActive =
        txFrom || txTo || txVendor !== "all" || txStatus !== "all" || txQuery;

    const applyPreset = (v) => {
        const now = new Date();
        const iso = (d) => d.toISOString().slice(0, 10);
        if (v === "30d") {
            const from = new Date();
            from.setDate(now.getDate() - 30);
            setTxFrom(iso(from));
            setTxTo(iso(now));
        } else if (v === "month") {
            setTxFrom(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
            setTxTo(iso(now));
        } else if (v === "ytd") {
            setTxFrom(iso(new Date(now.getFullYear(), 0, 1)));
            setTxTo(iso(now));
        } else if (v === "last-month") {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const end = new Date(now.getFullYear(), now.getMonth(), 0);
            setTxFrom(iso(start));
            setTxTo(iso(end));
        }
    };

    const clearFilters = () => {
        setTxFrom("");
        setTxTo("");
        setTxVendor("all");
        setTxStatus("all");
        setTxQuery("");
    };

    const exportTxCSV = () => {
        const rows = [
            [
                "Session ID",
                "Product",
                "Vendor",
                "Buyer",
                "Amount",
                "Currency",
                "Mode",
                "Payment mode",
                "Vendor share %",
                "Status",
                "Created",
                "Paid at",
            ],
            ...filteredTx.map((t) => [
                t.session_id,
                t.product_name || t.lookup_key || "",
                t.vendor || "",
                t.customer_email || "",
                ((t.amount || 0) / 100).toFixed(2),
                (t.currency || "hkd").toUpperCase(),
                t.mode || "",
                t.payment_mode || "",
                t.vendor_share_pct ?? "",
                t.payment_status || "",
                t.created_at || "",
                t.paid_at || "",
            ]),
        ];
        const csv = rows
            .map((r) =>
                r
                    .map((c) => {
                        const s = String(c ?? "");
                        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                    })
                    .join(",")
            )
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const tag = txVendor !== "all" ? `-${txVendor.replace(/\s+/g, "-")}` : "";
        a.download = `perx-transactions${tag}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportTxPDF = () => {
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        // Header band
        doc.setFillColor(0, 28, 80); // navy-700
        doc.rect(0, 0, pageW, 90, "F");
        doc.setTextColor(212, 175, 55); // gold-500
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text("PERX GROUP", 40, 34);
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont("times", "italic");
        doc.text("Statement of transactions", 40, 60);

        // Meta block
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        const today = new Date().toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
        doc.text(`Generated ${today}`, pageW - 40, 34, { align: "right" });
        const range =
            txFrom || txTo
                ? `${txFrom || "Beginning"} → ${txTo || "Today"}`
                : "All time";
        doc.text(`Period: ${range}`, pageW - 40, 50, { align: "right" });
        if (txVendor !== "all") {
            doc.text(`Vendor: ${txVendor}`, pageW - 40, 66, { align: "right" });
        }

        // Summary strip
        doc.setTextColor(0, 28, 80);
        doc.setFillColor(247, 244, 237); // cream
        doc.rect(40, 110, pageW - 80, 44, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        const paidRows = filteredTx.filter((t) => t.payment_status === "paid").length;
        const refundedRows = filteredTx.filter(
            (t) => t.payment_status === "refunded"
        ).length;
        const cells = [
            ["TRANSACTIONS", `${filteredTx.length}`],
            ["PAID", `${paidRows}`],
            ["REFUNDED", `${refundedRows}`],
            ["PAID TOTAL", money(filteredTotal, "hkd")],
        ];
        const cellW = (pageW - 80) / cells.length;
        cells.forEach((c, i) => {
            const x = 40 + cellW * i + 12;
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.setTextColor(120, 120, 130);
            doc.text(c[0], x, 128);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.setTextColor(0, 28, 80);
            doc.text(c[1], x, 146);
        });

        // Table
        const head = [[
            "Session",
            "Product",
            "Vendor",
            "Buyer",
            "Amount",
            "Mode",
            "Vendor %",
            "Status",
            "Date",
        ]];
        const body = filteredTx.map((t) => [
            (t.session_id || "").slice(-12),
            t.product_name || t.lookup_key || "",
            t.vendor || "—",
            t.customer_email || "—",
            money(t.amount, t.currency),
            t.payment_mode || t.mode || "—",
            t.vendor_share_pct != null ? `${t.vendor_share_pct}%` : "—",
            (t.payment_status || "").toUpperCase(),
            t.paid_at
                ? new Date(t.paid_at).toLocaleDateString()
                : new Date(t.created_at).toLocaleDateString(),
        ]);
        autoTable(doc, {
            head,
            body,
            startY: 170,
            margin: { left: 40, right: 40 },
            styles: { fontSize: 8, cellPadding: 6, textColor: [0, 28, 80] },
            headStyles: {
                fillColor: [0, 28, 80],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                fontSize: 8,
            },
            alternateRowStyles: { fillColor: [252, 250, 245] },
            didParseCell: (data) => {
                if (data.section === "body" && data.column.index === 7) {
                    const v = String(data.cell.raw || "");
                    if (v === "PAID") data.cell.styles.textColor = [22, 130, 96];
                    else if (v === "REFUNDED" || v === "FAILED")
                        data.cell.styles.textColor = [200, 40, 40];
                    else if (v === "PENDING") data.cell.styles.textColor = [180, 130, 30];
                }
            },
            didDrawPage: (data) => {
                doc.setFont("helvetica", "normal");
                doc.setFontSize(8);
                doc.setTextColor(140, 140, 150);
                doc.text(
                    `Perx Group · confidential vendor statement · page ${doc.internal.getCurrentPageInfo().pageNumber}`,
                    pageW / 2,
                    pageH - 20,
                    { align: "center" }
                );
            },
        });

        const tag = txVendor !== "all" ? `-${txVendor.replace(/\s+/g, "-")}` : "";
        doc.save(`perx-statement${tag}-${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    return (
        <>
            <PageHeader
                eyebrow="Payments"
                title="Transactions & purchase alerts"
                description="Every Stripe checkout — successful, failed, or pending — plus a live feed of new purchases."
                testid="admin-transactions-header"
                action={
                    notif.unread > 0 && (
                        <Badge
                            data-testid="admin-notif-unread-badge"
                            className="bg-gold-500 text-navy-900 border-gold-200 border text-xs px-3 py-1 uppercase tracking-widest"
                        >
                            {notif.unread} new
                        </Badge>
                    )
                }
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                <div className="grid sm:grid-cols-3 gap-5">
                    <StatTile
                        icon={Receipt}
                        label="Paid transactions"
                        value={paidCount}
                    />
                    <StatTile
                        icon={Receipt}
                        label="Gross revenue"
                        value={money(totalPaid, "hkd")}
                    />
                    <StatTile
                        icon={Bell}
                        label="Unread alerts"
                        value={notif.unread}
                        accent
                    />
                </div>

                <Tabs defaultValue={notif.unread > 0 ? "alerts" : "transactions"}>
                    <TabsList className="bg-white border border-navy-100 rounded-full h-11 p-1 mb-6">
                        <TabsTrigger
                            value="transactions"
                            data-testid="admin-tab-transactions"
                            className="rounded-full data-[state=active]:bg-navy-700 data-[state=active]:text-white px-5"
                        >
                            {transactions.length} transactions
                        </TabsTrigger>
                        <TabsTrigger
                            value="alerts"
                            data-testid="admin-tab-alerts"
                            className="rounded-full data-[state=active]:bg-navy-700 data-[state=active]:text-white px-5"
                        >
                            Purchase alerts ({notif.items.length})
                            {notif.unread > 0 && (
                                <span className="ml-2 inline-flex items-center justify-center rounded-full bg-gold-500 text-navy-900 text-[10px] font-bold h-5 min-w-5 px-1.5">
                                    {notif.unread}
                                </span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger
                            value="reimbursements"
                            data-testid="admin-tab-reimbursements"
                            className="rounded-full data-[state=active]:bg-navy-700 data-[state=active]:text-white px-5"
                        >
                            Vendor reimbursements
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="transactions">
                        <div
                            data-testid="tx-filters"
                            className="rounded-2xl bg-white border border-navy-100/70 shadow-soft p-5 mb-5"
                        >
                            <div className="flex flex-wrap items-end gap-4">
                                <div className="flex-1 min-w-[220px]">
                                    <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                        Search
                                    </Label>
                                    <div className="relative mt-1.5">
                                        <Search
                                            size={15}
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-500"
                                        />
                                        <Input
                                            data-testid="tx-search"
                                            value={txQuery}
                                            onChange={(e) => setTxQuery(e.target.value)}
                                            placeholder="Product, vendor, buyer, session…"
                                            className="pl-9 bg-white border-navy-100 focus-visible:ring-gold-500 h-10"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                        Vendor
                                    </Label>
                                    <Select value={txVendor} onValueChange={setTxVendor}>
                                        <SelectTrigger
                                            data-testid="tx-vendor"
                                            className="mt-1.5 bg-white border-navy-100 h-10 w-52"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            <SelectItem value="all">All vendors</SelectItem>
                                            {vendors.map((v) => (
                                                <SelectItem key={v} value={v}>
                                                    {v}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                        Status
                                    </Label>
                                    <Select value={txStatus} onValueChange={setTxStatus}>
                                        <SelectTrigger
                                            data-testid="tx-status"
                                            className="mt-1.5 bg-white border-navy-100 h-10 w-36"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            <SelectItem value="all">All</SelectItem>
                                            <SelectItem value="paid">Paid</SelectItem>
                                            <SelectItem value="pending">Pending</SelectItem>
                                            <SelectItem value="failed">Failed</SelectItem>
                                            <SelectItem value="expired">Expired</SelectItem>
                                            <SelectItem value="refunded">Refunded</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                        From
                                    </Label>
                                    <Input
                                        type="date"
                                        value={txFrom}
                                        onChange={(e) => setTxFrom(e.target.value)}
                                        max={txTo || undefined}
                                        data-testid="tx-from"
                                        className="mt-1.5 bg-white border-navy-100 focus-visible:ring-gold-500 h-10 w-44"
                                    />
                                </div>
                                <div>
                                    <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                        To
                                    </Label>
                                    <Input
                                        type="date"
                                        value={txTo}
                                        onChange={(e) => setTxTo(e.target.value)}
                                        min={txFrom || undefined}
                                        data-testid="tx-to"
                                        className="mt-1.5 bg-white border-navy-100 focus-visible:ring-gold-500 h-10 w-44"
                                    />
                                </div>
                            </div>
                            <div className="mt-4 flex flex-wrap items-center gap-2">
                                {[
                                    { v: "last-month", label: "Last month" },
                                    { v: "month", label: "This month" },
                                    { v: "30d", label: "Last 30 days" },
                                    { v: "ytd", label: "Year to date" },
                                ].map((p) => (
                                    <Button
                                        key={p.v}
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => applyPreset(p.v)}
                                        data-testid={`tx-preset-${p.v}`}
                                        className="rounded-full border-navy-100 text-navy-700 hover:bg-navy-50 h-9"
                                    >
                                        {p.label}
                                    </Button>
                                ))}
                                {filtersActive && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={clearFilters}
                                        data-testid="tx-clear"
                                        className="text-navy-500 h-9"
                                    >
                                        <X size={14} className="mr-1" /> Clear
                                    </Button>
                                )}
                            </div>
                            <div className="mt-4 pt-4 border-t border-navy-100/70 flex items-center justify-between gap-4 flex-wrap">
                                <div
                                    data-testid="tx-summary"
                                    className="text-sm text-navy-500"
                                >
                                    Showing{" "}
                                    <span className="text-navy-700 font-semibold">
                                        {filteredTx.length}
                                    </span>{" "}
                                    of {transactions.length} · Paid total{" "}
                                    <span className="text-navy-700 font-semibold">
                                        {money(filteredTotal, "hkd")}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={exportTxCSV}
                                        disabled={filteredTx.length === 0}
                                        data-testid="tx-export"
                                        className="rounded-full border-navy-100 text-navy-700 h-9"
                                    >
                                        <Download size={14} className="mr-1.5" /> Export CSV
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        onClick={exportTxPDF}
                                        disabled={filteredTx.length === 0}
                                        data-testid="tx-export-pdf"
                                        className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-9"
                                    >
                                        <Download size={14} className="mr-1.5" /> Statement PDF
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
                            <Table data-testid="admin-transactions-table">
                                <TableHeader>
                                    <TableRow className="bg-cream hover:bg-cream">
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Product</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Buyer</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Amount</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Mode</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Status</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Created</TableHead>
                                        <TableHead className="text-right text-navy-700 uppercase text-xs tracking-[0.2em]">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading && (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center text-navy-500 py-10">
                                                Loading…
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {!loading && filteredTx.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center text-navy-500 py-10">
                                                {transactions.length === 0
                                                    ? "No transactions yet. New Stripe checkouts will land here."
                                                    : "No transactions match your filters."}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {filteredTx.map((t) => (
                                        <TableRow key={t.session_id} data-testid={`tx-row-${t.session_id.slice(-8)}`}>
                                            <TableCell className="font-medium text-navy-700">
                                                {t.product_name || t.lookup_key}
                                                <div className="text-[11px] text-navy-500 font-normal font-mono mt-0.5">
                                                    {t.session_id.slice(0, 24)}…
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-navy-500">
                                                {t.customer_email || "—"}
                                            </TableCell>
                                            <TableCell className="text-navy-700 font-semibold">
                                                {money(t.amount, t.currency)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge className="bg-navy-50 text-navy-700 border-navy-100 border">
                                                    {t.mode || "—"}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    className={`${
                                                        STATUS_STYLE[t.payment_status] ||
                                                        STATUS_STYLE.pending
                                                    } border text-xs`}
                                                >
                                                    {t.payment_status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-navy-500 text-sm">
                                                {fmt(t.created_at)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {t.payment_status === "paid" && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        data-testid={`refund-btn-${t.session_id.slice(-8)}`}
                                                        onClick={() => {
                                                            setRefundTx(t);
                                                            setRefundReason("requested_by_customer");
                                                        }}
                                                        className="rounded-full h-8 border-red-200 text-red-600 hover:bg-red-50"
                                                    >
                                                        <RotateCcw size={12} className="mr-1.5" /> Refund
                                                    </Button>
                                                )}
                                                {t.payment_status === "refunded" && (
                                                    <span className="text-xs text-navy-500">
                                                        Refunded
                                                    </span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="alerts">
                        <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-navy-100/70">
                                <div className="text-sm text-navy-500">
                                    Live feed of purchases. Also mirrored via email to
                                    {" "}
                                    <span className="text-navy-700 font-mono text-xs">
                                        EMAIL_DESTINATION_ADDRESS
                                    </span>
                                    .
                                </div>
                                {notif.unread > 0 && (
                                    <Button
                                        data-testid="admin-mark-all-read"
                                        onClick={markAllRead}
                                        variant="outline"
                                        size="sm"
                                        className="rounded-full border-navy-100 text-navy-700 h-9"
                                    >
                                        <CheckCheck size={14} className="mr-1.5" /> Mark all read
                                    </Button>
                                )}
                            </div>
                            <div className="divide-y divide-navy-100/70">
                                {notif.items.length === 0 && (
                                    <div className="text-center text-navy-500 py-12">
                                        No purchase alerts yet.
                                    </div>
                                )}
                                {notif.items.map((n) => (
                                    <div
                                        key={n.id}
                                        data-testid={`admin-notif-${n.id}`}
                                        className={`p-5 flex items-start gap-4 ${
                                            n.read ? "opacity-70" : "bg-gold-500/5"
                                        }`}
                                    >
                                        <div
                                            className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                                n.read
                                                    ? "bg-navy-50 text-navy-500"
                                                    : "bg-gold-500/20 text-gold-700"
                                            }`}
                                        >
                                            <Receipt size={18} strokeWidth={1.7} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-medium text-navy-700">
                                                    New purchase:
                                                </span>
                                                <span className="text-navy-700">
                                                    {n.product_name}
                                                </span>
                                                <span className="text-gold-700 font-semibold">
                                                    {money(n.amount, n.currency)}
                                                </span>
                                                {!n.read && (
                                                    <Badge className="bg-gold-500 text-navy-900 text-[10px]">
                                                        NEW
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="text-xs text-navy-500 mt-1">
                                                {n.buyer_email || "unknown buyer"} · {fmt(n.created_at)}
                                            </div>
                                        </div>
                                        {!n.read && (
                                            <Button
                                                data-testid={`admin-notif-read-${n.id}`}
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => markRead(n.id)}
                                                className="text-navy-500 hover:text-navy-700 h-8"
                                            >
                                                Mark read
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="reimbursements">
                        <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-navy-100/70 gap-4 flex-wrap">
                                <div>
                                    <div className="font-display text-xl text-navy-700">
                                        End-of-month reimbursement report
                                    </div>
                                    <div className="text-sm text-navy-500 mt-1">
                                        Total billed to members grouped by vendor, plus the vendor&apos;s share owed.
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="month"
                                        value={month}
                                        onChange={(e) => setMonth(e.target.value)}
                                        data-testid="admin-reimb-month"
                                        className="h-10 rounded-full border border-navy-100 bg-white px-4 text-sm text-navy-700 focus:outline-none focus:ring-2 focus:ring-gold-500"
                                    />
                                    <Button
                                        data-testid="admin-reimb-export"
                                        onClick={() => exportReimbursementsCSV(reimbursement)}
                                        variant="outline"
                                        size="sm"
                                        className="rounded-full border-navy-100 text-navy-700 h-10"
                                    >
                                        <Download size={14} className="mr-1.5" /> Export CSV
                                    </Button>
                                </div>
                            </div>
                            {reimbursement && (
                                <div className="grid sm:grid-cols-3 gap-5 p-6 border-b border-navy-100/70">
                                    <ReimbSummary
                                        label="Gross billed"
                                        value={money(reimbursement.total_gross, reimbursement.currency)}
                                    />
                                    <ReimbSummary
                                        label="Total owed to vendors"
                                        value={money(
                                            reimbursement.total_reimbursement,
                                            reimbursement.currency
                                        )}
                                        accent
                                    />
                                    <ReimbSummary
                                        label="Perx net"
                                        value={money(
                                            reimbursement.total_gross -
                                                reimbursement.total_reimbursement,
                                            reimbursement.currency
                                        )}
                                    />
                                </div>
                            )}
                            <Table data-testid="admin-reimb-table">
                                <TableHeader>
                                    <TableRow className="bg-cream hover:bg-cream">
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Vendor</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Purchases</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Gross billed</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Reimbursement due</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Payment mode</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(!reimbursement || reimbursement.vendors.length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center text-navy-500 py-10">
                                                No paid deal transactions in this month.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {reimbursement?.vendors.map((v) => (
                                        <TableRow
                                            key={v.vendor}
                                            data-testid={`reimb-row-${v.vendor.replace(/\s+/g, "-").toLowerCase()}`}
                                        >
                                            <TableCell className="font-medium text-navy-700">
                                                {v.vendor}
                                                {v.vendor_email && (
                                                    <div className="text-[11px] text-navy-500 font-normal mt-0.5">
                                                        {v.vendor_email}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-navy-700">
                                                {v.purchases}
                                            </TableCell>
                                            <TableCell className="text-navy-700 font-semibold">
                                                {money(v.gross_billed, v.currency)}
                                            </TableCell>
                                            <TableCell className="text-gold-700 font-semibold">
                                                {money(v.vendor_reimbursement, v.currency)}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-wrap gap-1">
                                                    {Object.entries(v.payment_mode_breakdown).map(
                                                        ([mode, n]) => (
                                                            <Badge
                                                                key={mode}
                                                                className="bg-navy-50 text-navy-700 border-navy-100 border text-[10px]"
                                                            >
                                                                {mode} · {n}
                                                            </Badge>
                                                        )
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            <AlertDialog open={!!refundTx} onOpenChange={(o) => !o && setRefundTx(null)}>
                <AlertDialogContent className="bg-white">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Issue refund?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This calls Stripe to refund the full amount ({" "}
                            <strong>{refundTx && money(refundTx.amount, refundTx.currency)}</strong>
                            ) back to <strong>{refundTx?.customer_email || "the buyer"}</strong>.
                            The vendor job will be marked refunded and the reimbursement
                            report will exclude this transaction. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="py-2">
                        <div className="text-[10px] uppercase tracking-widest text-navy-500 mb-2">
                            Refund reason
                        </div>
                        <Select value={refundReason} onValueChange={setRefundReason}>
                            <SelectTrigger
                                data-testid="refund-reason"
                                className="bg-white border-navy-100 h-11"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                <SelectItem value="requested_by_customer">
                                    Requested by customer
                                </SelectItem>
                                <SelectItem value="duplicate">Duplicate charge</SelectItem>
                                <SelectItem value="fraudulent">Fraudulent</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="refund-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={submitRefund}
                            disabled={refunding}
                            data-testid="refund-confirm"
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            {refunding ? "Refunding…" : "Issue refund"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

function StatTile({ icon: Icon, label, value, accent }) {
    return (
        <div className="rounded-2xl bg-white shadow-soft p-6">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.3em] text-navy-500">{label}</div>
                <Icon
                    size={18}
                    className={accent ? "text-gold-600" : "text-navy-500"}
                    strokeWidth={1.7}
                />
            </div>
            <div className="font-display text-3xl text-navy-700">{value}</div>
        </div>
    );
}

function ReimbSummary({ label, value, accent }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-navy-500 mb-1.5">
                {label}
            </div>
            <div
                className={`font-display text-3xl ${
                    accent ? "text-gold-700" : "text-navy-700"
                }`}
            >
                {value}
            </div>
        </div>
    );
}

function exportReimbursementsCSV(report) {
    if (!report) return;
    const rows = [["Month", report.month]];
    rows.push([]);
    rows.push([
        "Vendor",
        "Vendor email",
        "Purchases",
        "Gross billed",
        "Reimbursement due",
        "Payment modes",
        "Currency",
    ]);
    for (const v of report.vendors) {
        rows.push([
            v.vendor,
            v.vendor_email || "",
            v.purchases,
            (v.gross_billed / 100).toFixed(2),
            (v.vendor_reimbursement / 100).toFixed(2),
            Object.entries(v.payment_mode_breakdown)
                .map(([m, n]) => `${m}:${n}`)
                .join(" "),
            (v.currency || "hkd").toUpperCase(),
        ]);
    }
    rows.push([]);
    rows.push(["Totals", "", "", (report.total_gross / 100).toFixed(2), (report.total_reimbursement / 100).toFixed(2)]);
    const csv = rows
        .map((r) =>
            r
                .map((c) => {
                    const s = String(c ?? "");
                    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                })
                .join(",")
        )
        .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `perx-reimbursements-${report.month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}
